// The asset_compression remote config flag, read the way the app reads its other flags: off when absent, off until the
// settings are read, and on only for the viewers its rules cover. Off, every model loads exactly as before. No three.js
// here: the decoders are imported only once the flag is on.
import { useEffect } from 'react';
import { readRemoteFlag, useRemoteFlag, useRemoteFlagState } from '@/modules/remoteConfig';

export const ASSET_COMPRESSION_FLAG = 'asset_compression';

/** Whether this browser can decode a compressed copy: meshopt and the basis transcoder are WebAssembly, run in workers. */
export function canDecodeCompressedModels(): boolean {
  return typeof WebAssembly === 'object'
    && typeof Worker === 'function'
    && typeof URL !== 'undefined'
    && typeof URL.createObjectURL === 'function';
}

/** Whether compressed copies are loaded for this viewer, for code outside React; never rejects. */
export async function assetCompressionOn(): Promise<boolean> {
  if (!canDecodeCompressedModels()) return false;
  try {
    return await readRemoteFlag(ASSET_COMPRESSION_FLAG);
  } catch {
    return false;
  }
}

/**
 * Whether compressed copies are loaded for this viewer; off until the remote config is read. `read` false (nothing
 * here would load a copy) leaves the settings unread, as they were: a viewer who is not signed in can't read them.
 */
export function useAssetCompression(read = true): boolean {
  return useRemoteFlag(ASSET_COMPRESSION_FLAG, read) && canDecodeCompressedModels();
}

/** As useAssetCompression, but undefined until the remote config is read, so a model waits for the answer. */
export function useAssetCompressionState(read = true): boolean | undefined {
  const flag = useRemoteFlagState(ASSET_COMPRESSION_FLAG, read);
  return canDecodeCompressedModels() ? flag : false;
}

/**
 * Starts the decoders (the basis transcoder's download, meshopt's WebAssembly) as soon as the flag is known to be on,
 * before a model needs them: the editor reads the settings as it opens. It never reads them itself.
 */
export function useStartModelDecoders(): void {
  const on = useAssetCompression(false);
  useEffect(() => {
    if (!on) return;
    void import('./modelDecoders').then(decoders => decoders.startModelDecoders()).catch(() => {
      // A model that needs them starts them itself, and falls back to its original if they never start
    });
  }, [on]);
}
